import React, { useRef } from 'react';

import { getCities } from 'src/components/ProjectOverview/utils/getCities';
import filterItems from 'src/components/ProjectOverview/utils/filterItems';

import { scrollToElement } from '@ads-core/utils';
import { useAdsContext } from '@ads-core/providers';
import { vars } from '@ads-core/theme';

import { createFilterMapper, baseQueryParams, slugify } from 'src/utils';
import { useParams } from 'src/hooks/useParams';

import { DEFAULT_PAGE_SIZE } from 'src/data/constants';
import { useSetParamsToRoute } from 'src/hooks/useSetParamsToRoute';

import { Box, Heading, PageGrid, GridItem, Card, Pagination } from '@ads-core/components';
import { ProjectOverviewProps } from 'components/ProjectOverview';

import { getProjectOverviewUrlParams } from './utils/getProjectOverviewUrlParams';
import { setCurrentPage } from './utils/setCurrentPage';
import { sortFilteredItems } from './utils/sortFilteredItems';

import { Filters } from './_Filters';
import { ResultsTable } from './_ResultsTable';

export const queryParams = {
  ...baseQueryParams,
};

export const ProjectOverview = (props: ProjectOverviewProps) => {
  const { setParamsToRoute } = useSetParamsToRoute();
  const adsContext = useAdsContext();
  const ref = useRef<HTMLDivElement>(null);
  const { city, region, page } = useParams();
  const { regionsFilter, items } = props.fields;
  const regionMapper = createFilterMapper(regionsFilter, 'id');
  const regionFilterValue = regionMapper[region] || '';
  const cityFilterValue = city || '';

  const currentFilterRegion = regionsFilter.find((item) => item.id === regionFilterValue)?.name;
  const currentFilterCity = items.find(
    (item) => slugify(item.fields.city.value) === cityFilterValue
  )?.fields.city.value;

  // Filter the filteredItems based on the region query param
  const filteredItems = filterItems({ items, city: cityFilterValue, region: regionFilterValue });
  const sortedItems = sortFilteredItems(filteredItems);

  // Filter the options based on the region query param
  const filterCityOptions = filterItems({ items, region: regionFilterValue });
  const sortedCityOptions = sortFilteredItems(filterCityOptions);
  const cityFilterOptions = getCities(sortedCityOptions);

  // Pagination of the table items
  const limit = DEFAULT_PAGE_SIZE;
  const amountOfPages = Math.ceil(sortedItems.length / limit);
  const currentPage = setCurrentPage(Number(page), amountOfPages);
  const tableItems = sortedItems.slice((currentPage - 1) * limit, currentPage * limit);

  return (
    <PageGrid>
      {props.fields.title ? (
        <GridItem columnStart={{ initial: '1', lg: '3' }} columnEnd={{ initial: '-1', lg: '-3' }}>
          <Box paddingBottom={{ initial: 4, md: 2 }} asChild>
            <Heading size="h1" as="h2">
              {props.fields.title.value}
            </Heading>
          </Box>
        </GridItem>
      ) : null}
      <GridItem columnEnd="1" columnStart="-1" ref={ref}>
        <Card>
          <Filters
            regionsFilterOptions={regionsFilter}
            cityFilterOptions={cityFilterOptions}
            currentFilterCity={currentFilterCity}
            currentFilterRegion={regionFilterValue}
          />
          <Box bg="backgroundLight" borderTopRightRadius="lg" width="100%" padding={10} ref={ref}>
            <ResultsTable
              items={tableItems}
              currentFilterRegion={currentFilterRegion}
              currentFilterCity={currentFilterCity}
            />
          </Box>
          {amountOfPages > 1 ? (
            <Box as="footer">
              <Box
                padding={6}
                borderBottomRightRadius="lg"
                borderBottomLeftRadius="lg"
                bg="containerLight"
                style={{ boxShadow: `inset ${vars.boxShadow.primary}` }}
              >
                <Pagination
                  forcePage={Number(currentPage) - 1}
                  pageCount={amountOfPages}
                  onPageChange={(e) => {
                    const selectedPage = e.selected + 1;

                    const urlParams = getProjectOverviewUrlParams({
                      city: currentFilterCity,
                      cityFilterOptions: cityFilterOptions,
                      region: regionFilterValue,
                      regionFilterOptions: regionsFilter,
                      page: selectedPage,
                    });

                    setParamsToRoute(urlParams);
                    scrollToElement(ref, adsContext.headerHeight);
                  }}
                />
              </Box>
            </Box>
          ) : null}
        </Card>
      </GridItem>
    </PageGrid>
  );
};
